import { InputBaseComponentProps } from "@mui/material/InputBase";
import { forwardRef, useState, useEffect, useRef, ChangeEvent, KeyboardEvent } from "react";

interface Props extends Omit<InputBaseComponentProps, "onChange"> {
  /** Decimal places displayed on screen (DB handles precision internally) */
  scale?: number;
  /** Max total input digits */
  maxDigits?: number;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

function formatDigits(digits: string, scale: number): string {
  if (!digits) return "";
  const padded = digits.padStart(scale + 1, "0");
  const intRaw = padded.slice(0, -scale).replace(/^0+/, "") || "0";
  const dec = padded.slice(-scale);
  const intFormatted = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${intFormatted},${dec}`;
}

function numberToDigits(value: unknown, scale: number): string {
  const num = parseFloat(String(value ?? ""));
  if (isNaN(num) || num === 0) return "";
  return Math.round(Math.abs(num) * Math.pow(10, scale)).toString();
}

function digitsToNumber(digits: string, scale: number): number {
  return digits ? parseInt(digits, 10) / Math.pow(10, scale) : 0;
}

/**
 * Currency input with right-to-left filling (bank-style).
 * - Digits fill from right: typing "1" → "0,001", "1000" → "1,000"
 * - Backspace removes last digit; select-all + Backspace clears field
 * - Syncs with Formik via onChange, storing numeric value as string
 * - decimal(13,3) compatible by default
 */
const CurrencyMaskInput = forwardRef<HTMLInputElement, Props>(
  function CurrencyMaskInput(
    {
      scale = 2,
      maxDigits = 12,
      onChange,
      name,
      value,
      onBlur,
      onFocus,
      disabled,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ownerState: _ownerState,
      ...other
    },
    ref
  ) {
    const [digits, setDigits] = useState(() => numberToDigits(value, scale));
    const prevExternalValue = useRef(value);

    // Sync only on genuine external changes (Formik reset / enableReinitialize).
    // Skip when the incoming value is just an echo of what we already emitted.
    useEffect(() => {
      if (prevExternalValue.current === value) return;
      prevExternalValue.current = value;

      const extNum = parseFloat(String(value ?? "")) || 0;
      const localNum = digitsToNumber(digits, scale);

      if (Math.abs(extNum - localNum) > 1e-9) {
        setDigits(numberToDigits(value, scale));
      }
      // `digits` intentionally omitted — we only want to react to external value changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, scale]);

    const emit = (newDigits: string) => {
      onChange?.({
        target: {
          name: name ?? "",
          value: String(digitsToNumber(newDigits, scale)),
        },
      } as ChangeEvent<HTMLInputElement>);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newDigits = e.target.value.replace(/\D/g, "").slice(0, maxDigits);
      setDigits(newDigits);
      emit(newDigits);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        const input = e.currentTarget;
        const isAllSelected =
          input.selectionStart === 0 && input.selectionEnd === input.value.length;
        const newDigits = isAllSelected ? "" : digits.slice(0, -1);
        setDigits(newDigits);
        emit(newDigits);
      }
    };

    return (
      <input
        {...(other as React.InputHTMLAttributes<HTMLInputElement>)}
        ref={ref}
        name={name}
        value={formatDigits(digits, scale)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        inputMode="numeric"
        autoComplete="off"
      />
    );
  }
);

export default CurrencyMaskInput;
